// React imports
import React, { FC, useEffect, useMemo, useReducer } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

// Third-party imports
import { toast } from 'react-toastify';

// Component imports
import Loading from '@/components/Loading';
import CageCardTemplateWrapper from '@/components/Modals/PrintCageCards/CageCardTemplateWrapper';
import Banner from '@/components/UI/Banner';
import Button from '@/components/UI/Button';
import APITable from '@/components/UI/Table/Reusable/APITable';

// Utility imports
import { updateColumns, updateSettings } from '@/components/UI/Table/Reusable/Cache.utils';
import { successToast } from '@/helpers';
import { _isNotEmpty } from '@/littledash';
import Http from '@/support/http';
import { api as apiRoute, web as webRoute } from '@/support/route';
import { createMultiSelectDoubleEncodingProcessor } from '@/utils/TableFilter.utils';

// Local imports
import {
  Cage,
  colonyCagesBulkActions,
  generateColumns,
  generateFilterOptions,
  reducer,
  CagesState,
  CagesAction,
} from './Cages.utils';

// TYPES AND INTERFACES

interface TableSettings {
  tables?: {
    [key: string]: {
      columns?: Record<string, boolean>;
    };
  };
}

interface CagesProps {
  addToStudy?: boolean;
  settings?: TableSettings;
}

// CONSTANTS

const PAGE_SIZES = [50, 100, 200, 300];
const DEFAULT_SORT_BY = { id: 'id', desc: true };
const INCLUDE_PARAMS = 'counts,meta,study_link,operations';
const SEARCH_PLACEHOLDER = 'Search by Cage No or Name';
const LOADING_TEXT = 'Fetching cages...';

// MAIN COMPONENT

const Cages: FC<CagesProps> = ({ addToStudy = false, settings = {} }) => {
  // STATE INITIALIZATION AND HOOKS
  const initialState = useMemo(
    (): CagesState => ({
      updating: false,
      apiErrors: false,
      data: [],
      columns: [],
      searchQuery: '',
      filterOptions: generateFilterOptions({ fields: [], metadata: [] }, addToStudy),
      filters: [],
      filterMatch: true,
      selectedRows: {},
      total_pages: 1,
      current_page: 1,
      per_page: 10,
      order: 'desc' as const,
      sort: ['catalog'],
      cageIdToPrint: null,
      actions: [],
      metadata: [],
    }),
    [addToStudy]
  );

  const [state, dispatch] = useReducer<React.Reducer<CagesState, CagesAction>>(reducer, initialState);

  const { columns, updating, apiErrors, filterOptions, cageIdToPrint, actions, metadata } = state;

  // Redux hooks
  const storeDispatch = useDispatch();
  const reduxTableName = addToStudy ? 'cagesAddToStudy' : 'colonyCages';

  const { studyId } = useParams();
  const navigate = useNavigate();

  // MEMOIZED VALUES

  const updatedSettings = useMemo(() => {
    if (_isNotEmpty(columns)) {
      // Ensure any derived columns (e.g: from measurements) are listed in the settings.
      return updateSettings(
        'animals',
        columns as Array<{ id: string }>,
        settings as unknown as Parameters<typeof updateSettings>[2]
      );
    }
    return settings;
  }, [columns, settings]);

  // EFFECTS

  useEffect(() => {
    const updatedColumns = updateColumns(reduxTableName, columns, settings);
    dispatch({
      type: 'SET_COLUMNS',
      data: updatedColumns,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings?.tables]);

  const putCage = async (
    id: number | string,
    selectedFlatRows?: Array<{ original: { id: number | string; name: string } }>
  ) => {
    dispatch({ type: 'SET_UPDATING', data: true });
    dispatch({ type: 'SET_ERROR', data: false });
    try {
      let cages;
      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      if (addToStudy && selectedFlatRows) {
        cages = selectedFlatRows.map(({ original: { id, name } }) => ({
          id,
          name,
          study_id: Number(studyId),
        }));
      } else {
        cages = [{ id, study_id: null }];
      }
      await Http.put(apiRoute('cages.updateMany'), {
        cages,
      });
      if (addToStudy) {
        navigate(webRoute('studies.cages', { id: studyId }));
        successToast(`Successfully added ${cages.length > 1 ? 'cages' : 'cage'} to study`);
      }
    } catch {
      dispatch({ type: 'SET_ERROR', data: true });
    } finally {
      dispatch({ type: 'SET_UPDATING', data: false });
    }
  };

  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const { data } = await Http.get(apiRoute('cages.filterOptions'));
        dispatch({
          type: 'SET_FILTER_OPTIONS',
          data: generateFilterOptions(data, addToStudy),
        });

        // Create a stable version of handleRemoveCageFromStudy to break circular dependency
        const stableHandleRemoveCageFromStudy = async (id: number | string, name: string) => {
          dispatch({ type: 'SET_UPDATING', data: true });
          dispatch({ type: 'SET_ERROR', data: false });
          try {
            await Http.put(apiRoute('cages.updateMany'), {
              cages: [{ id, study_id: null }],
            });
            storeDispatch({ type: 'CLOSE_MODAL' });
            toast.success(`Successfully removed ${name} from Study`);
          } catch {
            dispatch({ type: 'SET_ERROR', data: true });
          } finally {
            dispatch({ type: 'SET_UPDATING', data: false });
          }
        };

        dispatch({
          type: 'SET_COLUMNS',
          data: generateColumns(
            data,
            settings?.tables?.[reduxTableName]?.columns,
            actions,
            addToStudy,
            dispatch,
            storeDispatch,
            stableHandleRemoveCageFromStudy
          ),
        });
      } catch {
        dispatch({ type: 'SET_ERROR', data: true });
      }
    };

    fetchFilterOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // EVENT HANDLERS AND FUNCTIONS

  /**
   * Handles deletion of multiple cages
   * @param selectedTableRows Array of cages to delete
   * @param callback Function to call on successful deletion
   */
  const handleDelete = async (selectedTableRows: Array<Cage>, callback: (message: string) => void) => {
    try {
      await Http.delete(apiRoute('cages.destroyMany', {}), {
        data: {
          cage_ids: selectedTableRows.map(({ id }) => id),
        },
      });
      return callback('Successfully deleted!');
    } catch {
      dispatch({ type: 'SET_ERROR', data: true });
    }
  };

  /**
   * Renders custom actions for adding cages to study
   * @param param0 Object containing selected rows
   * @returns JSX element with add to study actions
   */
  const renderAddToStudyActions = ({
    selectedFlatRows,
  }: {
    selectedFlatRows: Array<{ original: { id: number | string; name: string } }>;
  }) => {
    if (addToStudy) {
      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      const disabled = !selectedFlatRows.length;
      return (
        <div className="flex pa1 mt4 mb2 justify-start">
          <Button
            disabled={disabled}
            tooltip={disabled ? 'Please select at least one cage to add to the study' : undefined}
            onClick={() => putCage(0, selectedFlatRows)}
          >
            Assign
          </Button>
          <Button className="ml2" plain url={webRoute('studies.cages', { id: studyId })}>
            Cancel
          </Button>
        </div>
      );
    }
  };

  const bulkActions = (tableProps: {
    useTableProps: { selectedFlatRows: Array<{ original: Cage }>; apiTable: { refetchTableData: () => Promise<void> } };
  }) => (!addToStudy ? colonyCagesBulkActions(tableProps, handleDelete, storeDispatch, navigate) : []);

  // OR-2531: When adding cages to a study with colony, filter out cages already in one.
  const filterOutStudy = {
    type: 'and',
    op: 'eq',
    filter: 'study',
    value: null,
  };

  const loadingTxt = LOADING_TEXT;

  return (
    <>
      <div className="ma4">
        {apiErrors && (
          <Banner critical dismiss={false} className="mw6 ma3">
            <h3 className="f5 normal lh-title pb2">There was an error fetching cages</h3>
            <p className="f6 pb3 lh-copy">If this error persists try again later or contact support.</p>
            <Button outline critical url={'mailto:support@benchling.com'}>
              Contact support
            </Button>
          </Banner>
        )}
        <div className={`cages ${updating ? 'ui__disabled' : ''}`}>
          {updating ? (
            <div style={{ height: 530 }}>
              <Loading txt={loadingTxt} />
            </div>
          ) : (
            <APITable
              apiInfo={{
                type: 'legacyInternalApi',
                route: apiRoute('cages.index'),
              }}
              customFilter={addToStudy ? filterOutStudy : null}
              includeParam={INCLUDE_PARAMS}
              defaultSortBy={DEFAULT_SORT_BY}
              columns={columns as any}
              customFilterProcessor={createMultiSelectDoubleEncodingProcessor(
                metadata as Array<{ id: string | number; field_type: string }>
              )}
              bulkActions={bulkActions}
              filterOptions={filterOptions}
              searchPlaceholderText={SEARCH_PLACEHOLDER}
              pageSizes={PAGE_SIZES}
              renderCustomActions={renderAddToStudyActions}
              settings={updatedSettings}
              reduxTableName={reduxTableName}
              loadingText={loadingTxt}
              data-testid="cages-api-table"
            />
          )}
        </div>
      </div>
      {/* eslint-disable-next-line @typescript-eslint/strict-boolean-expressions */}
      {cageIdToPrint && (
        <CageCardTemplateWrapper
          cageId={cageIdToPrint}
          onComplete={() => dispatch({ type: 'SET_CAGE_ID_TO_PRINT', data: null })}
        />
      )}
    </>
  );
};

export default Cages;
